<template>
  <AlternativesPageV2 v-if="page.slug === 'honeybook'" :page="page" />
  <AlternativesPage v-else :page="page" />
</template>

<script setup>
// One generated module per entry (modules/page-data.ts), so a page downloads only its own.
import alternativesLoaders from '#build/page-data/alternatives-loaders.mjs';
import { localizedUrl } from '~/data/locales';
import { ogImagePathForRoute } from '~/utils/og-image';
import AlternativesPage from '~/components/AlternativesPage.vue';
// HoneyBook renders the v2 template.
import AlternativesPageV2 from '~/components/AlternativesPageV2.vue';

const route = useRoute();
const { canonicalLocaleCode, isCurrentPageIndexable } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';

const page = await alternativesLoaders[route.params.slug]?.();

if (!page) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true });
}

const pagePath = `/alternatives/${page.slug}`;
const canonicalUrl = localizedUrl(siteUrl, pagePath, canonicalLocaleCode.value);
const image = `${siteUrl}${ogImagePathForRoute(pagePath)}`;
const imageAlt = `${page.eyebrow} — comparison by Workspace369`;
const alternativeTools = page.tools.filter((tool) => !tool.baseline);

useSeoMeta({
  title: page.metaTitle,
  description: page.metaDescription,
  author: page.author.name,
  ogType: 'article',
  ogTitle: page.metaTitle,
  ogDescription: page.metaDescription,
  ogUrl: canonicalUrl,
  ogImage: image,
  ogImageAlt: imageAlt,
  twitterCard: 'summary_large_image',
  twitterTitle: page.metaTitle,
  twitterDescription: page.metaDescription,
  twitterImage: image,
  twitterImageAlt: imageAlt,
  articlePublishedTime: page.publishedIso,
  articleModifiedTime: page.updatedIso,
});

useHead({
  meta: [
    {
      key: 'robots',
      name: 'robots',
      content: isCurrentPageIndexable.value ? 'index, follow' : 'noindex, follow',
    },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl }],
  script: [
    {
      key: `alternatives-schema-${page.slug}`,
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            '@id': `${canonicalUrl}#article`,
            headline: page.h1,
            description: page.metaDescription,
            url: canonicalUrl,
            inLanguage: canonicalLocaleCode.value,
            isAccessibleForFree: true,
            datePublished: page.publishedIso,
            dateModified: page.updatedIso,
            author: {
              '@type': 'Person',
              name: page.author.name,
              jobTitle: page.author.role,
              // Same founder entity as the blog guides (pages/blog/[slug].vue).
              ...(['Simon Dziak', 'Szymon Dziak'].includes(page.author.name)
                ? {
                    '@id': `${siteUrl}/about/#founder`,
                    url: `${siteUrl}/about/#founder`,
                    sameAs: ['https://simondziak.com/', 'https://www.linkedin.com/in/szymondziak/'],
                    image: `${siteUrl}/images/authors/simon-dziak-160.jpg`,
                  }
                : {}),
              worksFor: {
                '@type': 'Organization',
                name: 'Workspace369',
                url: siteUrl,
              },
            },
            publisher: {
              '@type': 'Organization',
              name: 'Workspace369',
              url: siteUrl,
              logo: {
                '@type': 'ImageObject',
                url: `${siteUrl}/favicon/android-chrome-512x512.png`,
              },
            },
            image,
            about: [
              { '@id': `${siteUrl}/#software` },
              {
                '@type': 'Thing',
                name: page.competitor,
                url: page.competitorUrl,
              },
            ],
            citation: (page.sources || []).map((source) => source.url),
            mainEntityOfPage: {
              '@id': `${canonicalUrl}#webpage`,
            },
          },
          {
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#alternatives`,
            name: page.eyebrow,
            itemListOrder: 'https://schema.org/ItemListUnordered',
            numberOfItems: alternativeTools.length,
            itemListElement: alternativeTools.map((tool, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: tool.name,
              // On-site anchors only: Google's carousel rules reject ListItems that link to another domain.
              url: `${canonicalUrl}#alt-tool-${tool.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
            })),
          },
          {
            '@type': 'FAQPage',
            '@id': `${canonicalUrl}#faq`,
            mainEntity: page.faqs.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${canonicalUrl}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${siteUrl}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Alternatives',
                item: `${siteUrl}/alternatives`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: page.eyebrow,
                item: canonicalUrl,
              },
            ],
          },
        ],
      }),
    },
  ],
});
</script>
